import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { ApiSuccessEnvelope } from "../../types/affiliates";
import {
  getAnalyticsInstance,
  type AffiliateAnalyticsDataSource,
} from "./analyticsDataSource";
import type { AffiliateChartRange } from "./useAffiliateTimeSeries";
import type { AffiliateAnalyticsFilters } from "./useAffiliateKpiCards";

// One ranked row (an affiliate or a promo code). `entityId` is the affiliate
// UUID / code string — used for the drill-in link on Top 5 Affiliates.
export interface AffiliateRankedItem {
  entityId: string;
  label: string | null;
  metrics: Record<string, number>;
}

interface AffiliateRankedData {
  items?: Array<{
    entity_id?: string;
    label?: string | null;
    metrics?: Record<string, number | string | boolean | null>;
  }>;
}

const TOP_LIMIT = 5;

const toNumberMetrics = (
  raw: Record<string, number | string | boolean | null> | undefined,
): Record<string, number> => {
  const out: Record<string, number> = {};
  Object.entries(raw ?? {}).forEach(([key, value]) => {
    if (value == null) return;
    const num = Number(value);
    if (!Number.isNaN(num)) out[key] = num;
  });
  return out;
};

const fetchRanked = async (
  route: string,
  range: AffiliateChartRange,
  metric: string,
  filters: AffiliateAnalyticsFilters,
  dataSource: AffiliateAnalyticsDataSource = "operator",
): Promise<AffiliateRankedItem[]> => {
  const params: Record<string, string | number> = {
    start_date: range.start_date,
    end_date: range.end_date,
    metric,
    limit: TOP_LIMIT,
  };
  if (filters.affiliate) params.affiliate = filters.affiliate;
  if (filters.promo_code) params.promo_code = filters.promo_code;
  const response = await getAnalyticsInstance(dataSource).get<
    ApiSuccessEnvelope<AffiliateRankedData>
  >(route, { params });
  return (response.data.data?.items ?? []).map((item) => ({
    entityId: item.entity_id ?? "",
    label: item.label ?? null,
    metrics: toNumberMetrics(item.metrics),
  }));
};

// Top 5 affiliates by the selected metric — module level only. No portal
// variant: an affiliate can't rank against its peers.
export const useTopAffiliates = (
  range: AffiliateChartRange,
  metric: string,
  filters: AffiliateAnalyticsFilters = {},
): UseQueryResult<AffiliateRankedItem[], Error> =>
  useQuery<AffiliateRankedItem[], Error>({
    queryKey: [
      "affiliate-kpis",
      "top-affiliates",
      metric,
      range.start_date,
      range.end_date,
      filters.affiliate ?? null,
      filters.promo_code ?? null,
    ],
    queryFn: () =>
      fetchRanked(
        API_ROUTES.AFFILIATE_ANALYTICS_TOP_AFFILIATES,
        range,
        metric,
        filters,
      ),
    enabled: Boolean(range.start_date && range.end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

const resolveTopPromoCodesRoute = (
  dataSource: AffiliateAnalyticsDataSource,
  affiliateId?: string,
): string => {
  // Portal routes carry no affiliate_id — the token scopes the query.
  if (dataSource === "portal") {
    return API_ROUTES.AFFILIATE_PORTAL_ANALYTICS_TOP_PROMO_CODES;
  }
  return affiliateId
    ? API_ROUTES.AFFILIATE_DETAIL_ANALYTICS_TOP_PROMO_CODES.replace(
        "{affiliate_id}",
        encodeURIComponent(affiliateId),
      )
    : API_ROUTES.AFFILIATE_ANALYTICS_TOP_PROMO_CODES;
};

// Top 5 promo codes by the selected metric. `affiliateId` present → the
// per-affiliate endpoint; absent → module level (all codes).
// `options.enabled: false` skips the fetch — for previews where the
// module fallback would be wrong.
export const useTopPromoCodes = (
  range: AffiliateChartRange,
  metric: string,
  affiliateId?: string,
  filters: AffiliateAnalyticsFilters = {},
  dataSource: AffiliateAnalyticsDataSource = "operator",
  options: { enabled?: boolean } = {},
): UseQueryResult<AffiliateRankedItem[], Error> =>
  useQuery<AffiliateRankedItem[], Error>({
    queryKey: [
      "affiliate-kpis",
      "top-promo-codes",
      dataSource,
      affiliateId ?? "",
      metric,
      range.start_date,
      range.end_date,
      filters.affiliate ?? null,
      filters.promo_code ?? null,
    ],
    queryFn: () =>
      fetchRanked(
        resolveTopPromoCodesRoute(dataSource, affiliateId),
        range,
        metric,
        filters,
        dataSource,
      ),
    enabled:
      (options.enabled ?? true) && Boolean(range.start_date && range.end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

export { fetchRanked, toNumberMetrics };
